import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  IconButton,
  Icons,
  SearchInput,
  SidePanel,
} from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { filterSectionsBySearch } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-search';
import {
  groupFilterSections,
  hasVaryingBounds,
  PricingRangeMode,
  PricingRangeTwins,
  resolveRangeMode,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-range-modes';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { PRICING_FILTER_PANEL_WIDTH } from '@sales-portal/domain/pricing/constants';
import { PricingFilterLastInvoiceSection } from '@sales-portal/domain/pricing/components/pricing-filter-last-invoice-section';
import { PricingFilterPillSection } from '@sales-portal/domain/pricing/components/pricing-filter-pill-section';
import { PricingFilterRangeSection } from '@sales-portal/domain/pricing/components/pricing-filter-range-section';
import { PricingFilterSearchEmptyState } from '@sales-portal/domain/pricing/components/pricing-filter-search-empty-state';
import {
  PricingFilterSection,
  PricingFilterSectionKind,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { UsePricingFiltersResult } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters';
import { View } from 'react-native';

const EMPTY_PILL_SELECTION: string[] = [];
const EMPTY_RANGE_BOUND = { max: null, min: null };

interface Props<T> {
  filters: UsePricingFiltersResult;
  onClose: () => void;
  sections: PricingFilterSection<T>[];
}

export const PricingFilterPanel = <T,>({
  filters,
  onClose,
  sections,
}: Props<T>) => {
  const [styles, theme] = useStyles();
  const [query, setQuery] = useState('');
  const [modeOverrides, setModeOverrides] = useState<
    Record<string, PricingRangeMode>
  >({});

  const visibleSections = useMemo(
    () => filterSectionsBySearch(sections, query),
    [sections, query],
  );

  const { clearSection, pills, ranges } = filters;

  const hasSelectedPills = useCallback(
    (sectionId: string): boolean => (pills[sectionId]?.length ?? 0) > 0,
    [pills],
  );

  // A $/% pair measures one "degree of change" two non-interchangeable ways,
  // so only one side may hold a bound — switching modes drops the other's.
  const handleModeChange = useCallback(
    (twins: PricingRangeTwins<T>, mode: PricingRangeMode) => {
      const leaving =
        mode === PricingRangeMode.Percent
          ? twins[PricingRangeMode.Currency]
          : twins[PricingRangeMode.Percent];
      clearSection(leaving.id);
      setModeOverrides((current) => ({
        ...current,
        [twins[PricingRangeMode.Currency].id]: mode,
      }));
    },
    [clearSection],
  );

  // Hide a section that can't discriminate rows (a range with one shared
  // value, or a pill with no options). An active selection keeps it visible
  // regardless, so it's always clearable.
  const isSectionVisible = useCallback(
    (section: PricingFilterSection<T>): boolean => {
      if (section.kind === PricingFilterSectionKind.Pill) {
        return section.options.length > 0 || hasSelectedPills(section.id);
      }
      if (section.kind === PricingFilterSectionKind.DateRange) {
        return true;
      }
      const bound = ranges[section.id];
      return (
        hasVaryingBounds(section) ||
        Boolean(bound && (bound.min !== null || bound.max !== null))
      );
    },
    [hasSelectedPills, ranges],
  );

  const groups = useMemo(
    () =>
      groupFilterSections(visibleSections).filter((group) =>
        group.twins
          ? Object.values(group.twins).some(isSectionVisible)
          : isSectionVisible(group.section),
      ),
    [isSectionVisible, visibleSections],
  );

  return (
    <SidePanel
      closeOnEscape
      onClose={onClose}
      width={PRICING_FILTER_PANEL_WIDTH}
    >
      <SidePanel.Header
        leadingActions={
          <IconButton
            aria-label={i18n.t('close')}
            icon={<Icons.X color={theme.sk.color.icon.primary} />}
            onPress={onClose}
            shape="rounded"
            size="$sm"
            variant="tertiary"
          />
        }
        title={
          <Typography variant="heading-sm">
            {i18n.t('pricing.filters.title')}
          </Typography>
        }
      />

      <SidePanel.Body paddingBottom={0} paddingHorizontal={0}>
        <View style={styles.body}>
          <View style={styles.searchInputWrapper}>
            <SearchInput
              clearAriaLabel={i18n.t('pricing.filters.clear')}
              onChangeText={setQuery}
              onClear={() => setQuery('')}
              placeholder={i18n.t('pricing.filters.search_placeholder')}
              value={query}
            />
          </View>
          {groups.length === 0 ? (
            <PricingFilterSearchEmptyState />
          ) : (
            <KeyboardAwareScrollView
              bottomOffset={theme.sk.spacing.spaceLarge}
              contentContainerStyle={styles.sections}
              style={styles.sectionsScroll}
            >
              {groups.map((group, index) => {
                const twins = group.twins;
                const mode = twins
                  ? resolveRangeMode(twins, modeOverrides, { pills, ranges })
                  : PricingRangeMode.Currency;
                const section = twins ? twins[mode] : group.section;

                return (
                  <React.Fragment key={group.id}>
                    {index > 0 ? <View style={styles.sectionDivider} /> : null}
                    {section.kind === PricingFilterSectionKind.Pill ? (
                      <PricingFilterPillSection
                        onClear={() => clearSection(section.id)}
                        onToggle={(value) =>
                          filters.togglePill(
                            section.id,
                            value,
                            section.singleSelect,
                          )
                        }
                        section={section}
                        selected={pills[section.id] ?? EMPTY_PILL_SELECTION}
                      />
                    ) : section.kind === PricingFilterSectionKind.DateRange ? (
                      <PricingFilterLastInvoiceSection
                        customBound={ranges[section.id] ?? EMPTY_RANGE_BOUND}
                        onClear={() => clearSection(section.id)}
                        onSelectBucket={(value) =>
                          filters.togglePill(section.id, value, true)
                        }
                        onSetCustomBound={(bound, value) =>
                          filters.setRangeBound(section.id, bound, value)
                        }
                        section={section}
                        selected={pills[section.id] ?? EMPTY_PILL_SELECTION}
                      />
                    ) : (
                      <PricingFilterRangeSection
                        bound={ranges[section.id] ?? EMPTY_RANGE_BOUND}
                        modeToggle={
                          twins
                            ? {
                                currencyAffix:
                                  twins[PricingRangeMode.Currency].affix,
                                mode,
                                onModeChange: (next) =>
                                  handleModeChange(twins, next),
                              }
                            : undefined
                        }
                        onClear={() => clearSection(section.id)}
                        onRangeChange={(bound, value) =>
                          filters.setRangeBound(section.id, bound, value)
                        }
                        section={section}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </KeyboardAwareScrollView>
          )}
        </View>
      </SidePanel.Body>
      {filters.hasActiveFilters ? (
        <SidePanel.Footer style={styles.footer}>
          <Button
            onPress={filters.clearAll}
            size="$md"
            variant="tertiary"
            width="100%"
          >
            {i18n.t('pricing.filters.clear_all')}
          </Button>
        </SidePanel.Footer>
      ) : null}
    </SidePanel>
  );
};

const useStyles = createStyles((theme) => ({
  body: {
    flex: 1,
  },
  footer: {
    borderTopColor: theme.sk.color.divider.default,
    borderTopWidth: 1,
  },
  searchInputWrapper: {
    borderBottomColor: theme.sk.color.divider.default,
    borderBottomWidth: 1,
    paddingBottom: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
  sectionDivider: {
    borderTopColor: theme.sk.color.divider.default,
    borderTopWidth: 1,
  },
  sections: {
    paddingBottom: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceLarge,
  },
  sectionsScroll: {
    flex: 1,
  },
}));
